import { useEffect, useRef } from 'react';
import { Segment } from '~/types';

interface TranscriptDisplayProps {
  segments: (Segment | null)[];
  pendingUploadCount: number;
}

export function TranscriptDisplay({ segments, pendingUploadCount }: TranscriptDisplayProps) {
  const containerRef = useRef<HTMLDivElement>(null);

  // Auto-scroll to bottom when new segments arrive
  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [segments.length]);

  const formatTime = (ms: number): string => {
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes
      .toString()
      .padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const getStatusColor = (status: string): string => {
    switch (status) {
      case 'done':
        return '#28a745';
      case 'running':
        return '#007bff';
      case 'queued':
        return '#6c757d';
      case 'error':
        return '#dc3545';
      default:
        return '#6c757d';
    }
  };

  const getStatusText = (status: string): string => {
    switch (status) {
      case 'done':
        return '完成';
      case 'running':
        return '转写中';
      case 'queued':
        return '队列中';
      case 'error':
        return '错误';
      default:
        return '未知';
    }
  };

  return (
    <div style={{ padding: '20px', borderBottom: '1px solid #ddd' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
        <h2>准实时转写</h2>
        {pendingUploadCount > 0 && (
          <div style={{ fontSize: '14px', color: '#6c757d' }}>
            待上传: {pendingUploadCount} 个分片
          </div>
        )}
      </div>

      <div
        ref={containerRef}
        style={{
          height: '400px',
          overflowY: 'auto',
          border: '1px solid #ddd',
          borderRadius: '4px',
          padding: '15px',
          backgroundColor: '#f8f9fa',
        }}
      >
        {segments.length === 0 && (
          <div style={{ textAlign: 'center', color: '#6c757d', padding: '20px' }}>
            等待转写结果...
          </div>
        )}

        {segments.map((segment, index) => {
          if (!segment) {
            return (
              <div
                key={index}
                style={{
                  marginBottom: '15px',
                  padding: '10px',
                  backgroundColor: '#fff',
                  borderLeft: '3px solid #6c757d',
                  borderRadius: '4px',
                }}
              >
                <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px' }}>
                  分片 #{index}
                </div>
                <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  [转写中...]
                </div>
              </div>
            );
          }

          return (
            <div
              key={index}
              style={{
                marginBottom: '15px',
                padding: '10px',
                backgroundColor: '#fff',
                borderLeft: `3px solid ${getStatusColor(segment.asr_status)}`,
                borderRadius: '4px',
              }}
            >
              <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '5px', display: 'flex', justifyContent: 'space-between' }}>
                <span>
                  分片 #{segment.segment_index} |{' '}
                  {formatTime(segment.start_ms)} - {formatTime(segment.end_ms)}
                </span>
                <span style={{ color: getStatusColor(segment.asr_status) }}>
                  {getStatusText(segment.asr_status)}
                </span>
              </div>

              {segment.asr_status === 'done' && segment.asr_text && (
                <div style={{ lineHeight: '1.6' }}>{segment.asr_text}</div>
              )}

              {segment.asr_status === 'error' && (
                <div style={{ color: '#dc3545', fontStyle: 'italic' }}>
                  错误: {segment.asr_error || '未知错误'}
                </div>
              )}

              {(segment.asr_status === 'queued' || segment.asr_status === 'running') && (
                <div style={{ color: '#6c757d', fontStyle: 'italic' }}>
                  [转写中...]
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
